import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './EventDetail.css';
import GeographicalMap, { MapPlace } from '../components/map/GeographicalMap';
import {
  Event,
  EventBadge,
  EventUser,
  addEventBadge,
  deleteEvent,
  fetchEventBadges,
  fetchEventById,
  fetchEventUsers,
  fetchPlaceById,
  inviteUserToEvent,
  removeEventBadge,
  searchPotentialUsers,
  updateEvent,
  UserSearchResult,
  PlaceInfo,
} from '../services/eventService';
import { fetchBadges, Badge } from '../services/badgeService';
import { fetchCurrentUser, UserProfile } from '../services/userService';
import { getPageTitle } from '../constants';

const ACCESS_LEVELS = ['PUBLIC', 'MEMBERS_ACCESSIBLE', 'PRIVATE'];

const toLocalInputValue = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value: string): number | null => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.getTime();
};

const formatDateTime = (iso: string | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface FormState {
  name: string;
  accessLevel: string;
  description: string;
  timeStart: string;
  timeFinish: string;
  useCoordinates: boolean;
  latitude: string;
  longitude: string;
  placeName: string;
}

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<Event | null>(null);
  const [place, setPlace] = useState<PlaceInfo | null>(null);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [eventBadges, setEventBadges] = useState<EventBadge[]>([]);
  const [participants, setParticipants] = useState<EventUser[]>([]);
  const [invitedInterested, setInvitedInterested] = useState<EventUser[]>([]);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState<FormState>({
    name: '',
    accessLevel: 'PUBLIC',
    description: '',
    timeStart: '',
    timeFinish: '',
    useCoordinates: false,
    latitude: '',
    longitude: '',
    placeName: '',
  });

  const [badgeToAdd, setBadgeToAdd] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedSearchEmail, setSelectedSearchEmail] = useState('');

  const isOwner = useMemo(() => {
    if (!event || !currentUser) return false;
    return event.organizerId != null && Number(event.organizerId) === Number(currentUser.id);
  }, [event, currentUser]);

  useEffect(() => {
    document.title = getPageTitle(event?.name ? `Event - ${event.name}` : 'Event');
  }, [event?.name]);

  const populateFormFromEvent = (evt: Event) => {
    setForm({
      name: evt.name || '',
      accessLevel: evt.accessLevel || 'PUBLIC',
      description: evt.description || '',
      timeStart: toLocalInputValue(evt.startDate),
      timeFinish: toLocalInputValue(evt.endDate),
      useCoordinates: !evt.placeId,
      latitude: evt.lat ? String(evt.lat) : '',
      longitude: evt.lng ? String(evt.lng) : '',
      placeName: evt.location || '',
    });
  };

  const loadAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [evt, badges, evtBadges, participantsList, invitedList, me] = await Promise.all([
        fetchEventById(id),
        fetchBadges(),
        fetchEventBadges(id),
        fetchEventUsers(id, 'PARTICIPANT'),
        fetchEventUsers(id, 'INVITED_INTERESTED'),
        fetchCurrentUser().catch(() => null),
      ]);

      if (!evt) {
        setErrorMsg('Event not found');
        setEvent(null);
        return;
      }

      setEvent(evt);
      setAllBadges(badges);
      setEventBadges(evtBadges);
      setParticipants(participantsList);
      setInvitedInterested(invitedList);
      setCurrentUser(me);

      if (evt.placeId) {
        fetchPlaceById(evt.placeId).then(setPlace).catch(() => setPlace(null));
      } else {
        setPlace(null);
      }

      populateFormFromEvent(evt);
    } catch (err) {
      console.error('Failed to load event', err);
      setErrorMsg('Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleFormChange = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value as never }));
  };

  const handleSave = async () => {
    if (!event) return;
    setErrorMsg(null);
    setSuccessMsg(null);
    const timeStartMs = fromLocalInputValue(form.timeStart);
    if (timeStartMs == null) {
      setErrorMsg('Start time is required');
      return;
    }
    try {
      const payload = {
        id: Number(event.id),
        name: form.name,
        accessLevel: form.accessLevel,
        description: form.description || undefined,
        timeStart: timeStartMs,
        timeFinish: fromLocalInputValue(form.timeFinish),
        placeId: form.useCoordinates ? null : event.placeId ?? null,
        latitude: form.useCoordinates && form.latitude ? Number(form.latitude) : null,
        longitude: form.useCoordinates && form.longitude ? Number(form.longitude) : null,
        placeName: form.useCoordinates ? form.placeName || null : null,
      };
      const updated = await updateEvent(payload);
      if (updated) {
        setEvent(updated);
        populateFormFromEvent(updated);
        setSuccessMsg('Event saved');
        setEditMode(false);
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || 'Failed to save event');
    }
  };

  const handleCancelEdit = () => {
    if (event) populateFormFromEvent(event);
    setEditMode(false);
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const handleDelete = async () => {
    if (!event) return;
    if (!window.confirm('Delete this event?')) return;
    const ok = await deleteEvent(event.id, true);
    if (ok) {
      navigate('/events');
    } else {
      setErrorMsg('Failed to delete event');
    }
  };

  const handleAddBadge = async () => {
    if (!event || !badgeToAdd) return;
    const created = await addEventBadge(event.id, Number(badgeToAdd));
    if (created) {
      const refreshed = await fetchEventBadges(event.id);
      setEventBadges(refreshed);
      setBadgeToAdd('');
    } else {
      setErrorMsg('Could not add badge');
    }
  };

  const handleRemoveBadge = async (badgeId: number) => {
    if (!event) return;
    const ok = await removeEventBadge(event.id, badgeId);
    if (ok) {
      setEventBadges((prev) => prev.filter((b) => b.badgeId !== badgeId));
    }
  };

  useEffect(() => {
    if (!event) return;
    if (searchText.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    let ignore = false;
    const t = setTimeout(async () => {
      const results = await searchPotentialUsers(searchText, event.id);
      if (!ignore) setSearchResults(results);
    }, 250);
    return () => {
      ignore = true;
      clearTimeout(t);
    };
  }, [searchText, event]);

  const handleInvite = async () => {
    if (!event || !selectedSearchEmail) return;
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const message = await inviteUserToEvent(event.id, selectedSearchEmail);
      setSuccessMsg(message || 'User invited');
      setSearchText('');
      setSearchResults([]);
      setSelectedSearchEmail('');
      const [participantsList, invitedList] = await Promise.all([
        fetchEventUsers(event.id, 'PARTICIPANT'),
        fetchEventUsers(event.id, 'INVITED_INTERESTED'),
      ]);
      setParticipants(participantsList);
      setInvitedInterested(invitedList);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || 'Failed to invite user');
    }
  };

  const mapPlaces: MapPlace[] = useMemo(() => {
    if (!event || !event.lat || !event.lng) return [];
    return [{
      id: event.id,
      name: event.name,
      address: place?.name || event.location || '',
      description: event.description || '',
      sportType: '',
      placeType: place?.assignment || '',
      lat: event.lat,
      lng: event.lng,
    }];
  }, [event, place]);

  if (loading) {
    return (
      <div className="event-detail-card">
        <p>Loading event…</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-card">
        <p>{errorMsg || 'Event not found'}</p>
      </div>
    );
  }

  const placeLabel = place?.name || event.location || (event.lat && event.lng ? `${event.lat}, ${event.lng}` : '—');

  return (
    <div className="event-detail-wrapper">
      <header className="event-hero">
        <div className="event-hero-main">
          <h1 className="event-hero-title">{event.name}</h1>
          <div className="event-hero-meta">
            {event.organizer && <span>👤 {event.organizer}</span>}
            <span>🕒 {formatDateTime(event.startDate)}{event.endDate ? ` – ${formatDateTime(event.endDate)}` : ''}</span>
            {event.accessLevel && <span className={`access-pill access-${event.accessLevel.toLowerCase()}`}>{event.accessLevel}</span>}
          </div>
        </div>
        {isOwner && !editMode && (
          <div className="event-hero-actions">
            <button type="button" className="event-action-btn primary" onClick={() => setEditMode(true)}>
              Edit
            </button>
            <button type="button" className="event-action-btn danger" onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </header>

      {errorMsg && <div className="message-banner error">{errorMsg}</div>}
      {successMsg && <div className="message-banner success">{successMsg}</div>}

      <div className="event-detail-grid">
        <section className="event-detail-card">
          <div className="map-area event-map">
            <GeographicalMap
              places={mapPlaces}
              center={event.lat && event.lng ? [event.lat, event.lng] : undefined}
              getPlaceDetailUrl={(p) => `/event/${p.id}`}
            />
          </div>
          <div className="place-info-block">
            <h4>{placeLabel}</h4>
            {event.lat && event.lng && (
              <p>Coordinates: {event.lat.toFixed(5)}, {event.lng.toFixed(5)}</p>
            )}
            {place?.assignment && <p>Type: {place.assignment}</p>}
            {place?.comment && <p>{place.comment}</p>}
          </div>
        </section>

        <section className="event-detail-card">
          {!editMode ? (
            <dl className="event-info-list">
              <dt>Description</dt>
              <dd>{event.description || <span className="muted">No description</span>}</dd>
              <dt>Place</dt>
              <dd>{placeLabel}</dd>
              <dt>Start</dt>
              <dd>{formatDateTime(event.startDate)}</dd>
              <dt>Finish</dt>
              <dd>{event.endDate ? formatDateTime(event.endDate) : <span className="muted">—</span>}</dd>
            </dl>
          ) : (
            <form className="event-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="accessLevel">Access level</label>
                <select
                  id="accessLevel"
                  className="form-select"
                  value={form.accessLevel}
                  onChange={(e) => handleFormChange('accessLevel', e.target.value)}
                >
                  {ACCESS_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>

              <div className="coords-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={form.useCoordinates}
                    onChange={(e) => handleFormChange('useCoordinates', e.target.checked)}
                  />
                  {' '}Use custom coordinates
                </label>
              </div>

              {form.useCoordinates && (
                <div className="form-group">
                  <label>Coordinates</label>
                  <div className="coords-grid">
                    <input
                      type="number"
                      step="any"
                      placeholder="Latitude"
                      value={form.latitude}
                      onChange={(e) => handleFormChange('latitude', e.target.value)}
                    />
                    <input
                      type="number"
                      step="any"
                      placeholder="Longitude"
                      value={form.longitude}
                      onChange={(e) => handleFormChange('longitude', e.target.value)}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Place name"
                    value={form.placeName}
                    onChange={(e) => handleFormChange('placeName', e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="timeStart">Start time</label>
                <input
                  id="timeStart"
                  type="datetime-local"
                  value={form.timeStart}
                  onChange={(e) => handleFormChange('timeStart', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="timeFinish">Finish time</label>
                <input
                  id="timeFinish"
                  type="datetime-local"
                  value={form.timeFinish}
                  onChange={(e) => handleFormChange('timeFinish', e.target.value)}
                />
              </div>

              <div className="event-actions">
                <button type="submit" className="event-action-btn primary">Save</button>
                <button type="button" className="event-action-btn ghost" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="badges-section">
            <h3>Badges</h3>
            <div className="badge-list">
              {eventBadges.length === 0 ? (
                <span className="badge-pill empty">No badges</span>
              ) : (
                eventBadges.map((b) => (
                  <span key={`${b.eventId}-${b.badgeId}`} className="badge-pill">
                    {b.name || b.badgeId}
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => handleRemoveBadge(b.badgeId)}
                        title="Remove"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
            {isOwner && (
              <div className="add-badge-row">
                <select
                  className="form-select"
                  value={badgeToAdd}
                  onChange={(e) => setBadgeToAdd(e.target.value)}
                >
                  <option value="">Select a badge…</option>
                  {allBadges
                    .filter((b) => !eventBadges.some((eb) => eb.badgeId === b.id))
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="event-action-btn primary"
                  disabled={!badgeToAdd}
                  onClick={handleAddBadge}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="event-detail-grid">
        <section className="event-detail-card">
          <h3 className="section-title">Participants ({participants.length})</h3>
          {participants.length === 0 ? (
            <p className="no-results">No participants yet</p>
          ) : (
            <div className="user-list">
              {participants.map((u) => (
                <div className="user-row" key={`p-${u.id}`}>
                  <span className="user-name">{u.alias}</span>
                  {u.email && <span className="user-email">{u.email}</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="event-detail-card">
          <h3 className="section-title">Invited & interested ({invitedInterested.length})</h3>
          {invitedInterested.length === 0 ? (
            <p className="no-results">Nobody invited yet</p>
          ) : (
            <div className="user-list">
              {invitedInterested.map((u) => (
                <div className="user-row" key={`i-${u.id}`}>
                  <span className="user-name">{u.alias}</span>
                  {u.email && <span className="user-email">{u.email}</span>}
                </div>
              ))}
            </div>
          )}

          {currentUser && (
            <div className="invite-form">
              <label htmlFor="inviteSearch">Invite a user</label>
              <input
                id="inviteSearch"
                type="text"
                placeholder="Type at least 3 characters…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <div className="found-count">{searchResults.length} found</div>
              <div className="invite-results-row">
                <select
                  className="form-select"
                  value={selectedSearchEmail}
                  onChange={(e) => setSelectedSearchEmail(e.target.value)}
                  disabled={searchResults.length === 0}
                >
                  <option value="">— pick a user —</option>
                  {searchResults.map((u) => (
                    <option key={u.id} value={u.email || ''}>
                      {u.fullName}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="event-action-btn primary"
                  onClick={handleInvite}
                  disabled={!selectedSearchEmail}
                >
                  Invite
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default EventDetailPage;